/**
 * Regeln, Zeitplaene, Wartungsplaene und Webhooks -- F3-11 bis F3-19.
 *
 * Vier Bestaende in einer Datei, aus demselben Grund wie bei den Makros: Sie
 * gehoeren zusammen, teilen sich dieselbe Aktionsliste und dieselbe
 * Bedingungssprache.
 */

import type { components } from '../../api/schema';
import { request } from '../../lib/apiClient';

export type AutomationRule = components['schemas']['AutomationRuleRead'];
export type AutomationRuleCreate = components['schemas']['AutomationRuleCreate'];
export type AutomationRuleUpdate = components['schemas']['AutomationRuleUpdate'];
export type RuleTestResult = components['schemas']['AutomationRuleTestResult'];
export type RuleFields = components['schemas']['RuleFieldsRead'];

export type ScheduledJob = components['schemas']['ScheduledJobRead'];
export type ScheduledJobCreate = components['schemas']['ScheduledJobCreate'];
export type ScheduledJobUpdate = components['schemas']['ScheduledJobUpdate'];
export type ScheduledJobRunResult = components['schemas']['ScheduledJobRunResult'];

export type RecurringTicket = components['schemas']['RecurringTicketRead'];
export type RecurringTicketCreate = components['schemas']['RecurringTicketCreate'];
export type RecurringTicketUpdate = components['schemas']['RecurringTicketUpdate'];
export type RecurringRunResult = components['schemas']['RecurringTicketRunResult'];

export type Webhook = components['schemas']['WebhookRead'];
export type WebhookCreate = components['schemas']['WebhookCreate'];
export type WebhookUpdate = components['schemas']['WebhookUpdate'];
export type WebhookDelivery = components['schemas']['WebhookDeliveryRead'];
export type WebhookEvent = components['schemas']['WebhookEventRead'];
export type DeliveryRunResult = components['schemas']['WebhookDeliveryRunResult'];

// -- Regeln -------------------------------------------------------------------

export function listRules(): Promise<AutomationRule[]> {
  return request<AutomationRule[]>('/automation-rules');
}

export function listRuleFields(): Promise<RuleFields> {
  return request<RuleFields>('/automation-rules/fields');
}

export function createRule(payload: AutomationRuleCreate): Promise<AutomationRule> {
  return request<AutomationRule>('/automation-rules', { method: 'POST', body: payload });
}

export function updateRule(ruleId: string, payload: AutomationRuleUpdate): Promise<AutomationRule> {
  return request<AutomationRule>(`/automation-rules/${ruleId}`, {
    method: 'PATCH',
    body: payload,
  });
}

export function deleteRule(ruleId: string): Promise<undefined> {
  return request<undefined>(`/automation-rules/${ruleId}`, { method: 'DELETE' });
}

export function testRule(ruleId: string, ticketId: string): Promise<RuleTestResult> {
  return request<RuleTestResult>(`/automation-rules/${ruleId}/actions/test`, {
    method: 'POST',
    body: { ticket_id: ticketId },
  });
}

// -- Zeitplaene ---------------------------------------------------------------

export function listJobs(): Promise<ScheduledJob[]> {
  return request<ScheduledJob[]>('/scheduled-jobs');
}

export function createJob(payload: ScheduledJobCreate): Promise<ScheduledJob> {
  return request<ScheduledJob>('/scheduled-jobs', { method: 'POST', body: payload });
}

export function updateJob(jobId: string, payload: ScheduledJobUpdate): Promise<ScheduledJob> {
  return request<ScheduledJob>(`/scheduled-jobs/${jobId}`, { method: 'PATCH', body: payload });
}

export function deleteJob(jobId: string): Promise<undefined> {
  return request<undefined>(`/scheduled-jobs/${jobId}`, { method: 'DELETE' });
}

export function runJobs(): Promise<ScheduledJobRunResult[]> {
  return request<ScheduledJobRunResult[]>('/scheduled-jobs/actions/run', { method: 'POST' });
}

// -- Wartungsplaene -----------------------------------------------------------

export function listRecurring(): Promise<RecurringTicket[]> {
  return request<RecurringTicket[]>('/recurring-tickets');
}

export function createRecurring(payload: RecurringTicketCreate): Promise<RecurringTicket> {
  return request<RecurringTicket>('/recurring-tickets', { method: 'POST', body: payload });
}

export function updateRecurring(
  recurringId: string,
  payload: RecurringTicketUpdate,
): Promise<RecurringTicket> {
  return request<RecurringTicket>(`/recurring-tickets/${recurringId}`, {
    method: 'PATCH',
    body: payload,
  });
}

export function deleteRecurring(recurringId: string): Promise<undefined> {
  return request<undefined>(`/recurring-tickets/${recurringId}`, { method: 'DELETE' });
}

export function runRecurring(): Promise<RecurringRunResult[]> {
  return request<RecurringRunResult[]>('/recurring-tickets/actions/run', { method: 'POST' });
}

// -- Webhooks -----------------------------------------------------------------

export function listWebhooks(): Promise<Webhook[]> {
  return request<Webhook[]>('/webhooks');
}

export function listWebhookEvents(): Promise<WebhookEvent[]> {
  return request<WebhookEvent[]>('/webhook-events');
}

export function createWebhook(payload: WebhookCreate): Promise<Webhook> {
  return request<Webhook>('/webhooks', { method: 'POST', body: payload });
}

export function updateWebhook(webhookId: string, payload: WebhookUpdate): Promise<Webhook> {
  return request<Webhook>(`/webhooks/${webhookId}`, { method: 'PATCH', body: payload });
}

export function deleteWebhook(webhookId: string): Promise<undefined> {
  return request<undefined>(`/webhooks/${webhookId}`, { method: 'DELETE' });
}

export function listDeliveries(webhookId: string): Promise<WebhookDelivery[]> {
  return request<WebhookDelivery[]>(`/webhooks/${webhookId}/deliveries`);
}

export function deliverWebhooks(): Promise<DeliveryRunResult> {
  return request<DeliveryRunResult>('/webhooks/actions/deliver', { method: 'POST' });
}
